import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  default as api,
  RecurrenceTaskStatusEnum,
  type TaskApprovalRequest,
} from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerApprovalsState {
  approvals: TaskApprovalRequest[];
  isLoading: boolean;
  error: string | null;
}

interface ManagerApprovalsActions {
  fetchApprovals: () => Promise<void>;
  approveTask: (taskId: string, recurrenceId: string) => Promise<void>;
  rejectTask: (taskId: string, recurrenceId: string) => Promise<void>;
  clearApprovals: () => void;
}

type ManagerApprovalsStore = ManagerApprovalsState & ManagerApprovalsActions;

type ManagerApprovalsStorePersist = Pick<ManagerApprovalsStore, 'approvals'>;

const useManagerApprovalsStore = create<ManagerApprovalsStore>()(
  persist<
    ManagerApprovalsStore,
    [],
    [],
    ManagerApprovalsStorePersist
  >((set, get) => ({
      approvals: [],
      isLoading: false,
      error: null,

      fetchApprovals: async () => {
        const hasCachedApprovals = get().approvals.length > 0;
        set({ isLoading: !hasCachedApprovals, error: null });

        try {
          const approvals = await api.tasksGetApprovalRequests();
          set({
            approvals,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to fetch approval requests',
            isLoading: false,
          });
        }
      },

      approveTask: async (taskId: string) => {
        try {
          await api.approveTask(taskId, RecurrenceTaskStatusEnum.Succeed);

          // Remove from approvals list
          set(state => ({
            approvals: state.approvals.filter(
              approval => approval._id !== taskId,
            ),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to approve task',
          );
        }
      },

      rejectTask: async (taskId: string) => {
        try {
          await api.rejectTask(taskId);

          // Remove from approvals list
          set(state => ({
            approvals: state.approvals.filter(
              approval => approval._id !== taskId,
            ),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to reject task',
          );
        }
      },

      clearApprovals: () =>
        set({
          approvals: [],
          error: null,
        }),
    }),
    {
      name: 'manager-approvals-store',
      storage: createPersistStorage<ManagerApprovalsStorePersist>(),
      partialize: state => ({
        approvals: state.approvals,
      }),
    },
  ),
);

export default useManagerApprovalsStore;
